<template>
  <div class="kf-inline">
    <v-progress-linear
      v-if="loading"
      indeterminate
      height="2"
      color="primary"
      class="kf-inline__bar"
    />

    <div v-if="!days.length" class="kf-inline__note">
      <template v-if="error">
        Couldn't load this borrower's messages.
        <v-btn small text color="primary" class="kf-inline__btn" @click="load">Retry</v-btn>
      </template>
      <template v-else-if="loading">Loading messages…</template>
      <template v-else>The agent has no days for this borrower.</template>
    </div>

    <template v-else>
      <table class="kf-inline__table">
        <colgroup>
          <col style="width: 120px" />
          <col style="width: 30%" />
          <col />
          <col style="width: 26%" />
          <col style="width: 130px" />
        </colgroup>
        <thead>
          <tr>
            <th>When (IST)</th>
            <th>What happened</th>
            <th>Message</th>
            <th>Why</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="day in visible" :key="day.kind + ':' + day.id">
            <td data-label="When" class="kf-nowrap">
              <div class="kf-strong">{{ dayIst(day.day) }}</div>
              <div class="kf-detail">{{ timeIst(day.at) }}</div>
            </td>
            <td data-label="What happened">{{ day.day_line || "-" }}</td>
            <td data-label="Message">
              <div v-if="day.message_preview" class="kf-message" :title="day.template_name">
                <div>“{{ day.message_preview }}”</div>
                <div class="kf-detail kf-channel">
                  <LucideIcon :name="channelIcon(day.channel_used)" :size="13" />
                  via {{ channelLabel(day.channel_used) }}
                </div>
              </div>
              <span v-else class="kf-muted">No message</span>
            </td>
            <!-- The model's own note is on hover, as on the old day list. -->
            <td data-label="Why">
              <div v-if="day.simple_reason" :title="day.reason">{{ day.simple_reason }}</div>
              <div v-else class="kf-muted">{{ day.verdict_label || "-" }}</div>
              <!-- No approved message fitted: what template would have. -->
              <TemplateGapNote v-if="day.gap" :gap="day.gap" class="kf-gap" />
              <div v-if="day.time_reason" class="kf-detail">Time: {{ day.time_reason }}</div>
            </td>
            <td data-label="Status">
              <StatusChip :status="day.status_label" :color="stateColor(day.state)" small />
              <div v-if="day.drop_label && day.state !== 'sent'" class="kf-detail">
                {{ day.drop_label }}
              </div>
              <div v-if="day.rehearsal && markRehearsals" class="kf-detail">practice run</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="days.length > visible.length" class="kf-inline__more">
        Showing {{ visible.length }} of {{ days.length }} days.
        <v-btn small text color="primary" class="kf-inline__btn" @click="showAll = true">
          Show all {{ days.length }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * One borrower's days with the growth agent, open under their row on the
 * Agent Tracker: each day's message (or none), what happened in one line, the
 * plain why, and how it ended. Loaded once, when the row opens.
 */
import StatusChip from "./StatusChip.vue";
import TemplateGapNote from "./TemplateGapNote.vue";
import api from "@/app-js/karrofin/api";
import {
  STATE_COLORS, channelIcon, channelLabel, dayIst, timeIst,
} from "@/pages/karrofin/agent/common";

/** Days shown when the row opens; "Show all" draws the rest. */
const FIRST = 10;

export default {
  name: "KfAgentUserDays",
  components: { StatusChip, TemplateGapNote },
  props: {
    userId: { type: Number, required: true },
    /* A live box marks each practice run; in test mode every row is one. */
    markRehearsals: { type: Boolean, default: false },
  },
  data() {
    return { days: [], loading: false, error: null, showAll: false };
  },
  computed: {
    visible() {
      return this.showAll ? this.days : this.days.slice(0, FIRST);
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = null;
      api
        .getGrowthUserHistory({ user_id: this.userId })
        .then((data) => {
          this.days = data.results || [];
        })
        .catch((err) => {
          this.error = err;
          this.days = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    stateColor(state) {
      return STATE_COLORS[state] || "";
    },
    channelIcon,
    channelLabel,
    dayIst,
    timeIst,
  },
};
</script>

<style scoped>
/* The same lighter ground and left rail as the User history lists, so the
   days read as belonging to the borrower above. */
.kf-inline {
  position: relative;
  padding: 4px 16px 8px 69px;
  background: var(--kf-surface-2);
  border-left: 3px solid #194e76;
  border-bottom: 1px solid var(--kf-border);
}
.kf-inline__bar {
  position: absolute;
  top: 0;
  left: 0;
}
.kf-inline__note,
.kf-inline__more {
  padding: 8px 0 2px;
  font-size: var(--kf-fs-meta);
  color: var(--kf-text-muted);
}
.kf-inline__btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}
.kf-inline__table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.kf-inline__table th {
  padding: 6px 12px 4px 0;
  text-align: left;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  white-space: nowrap;
}
.kf-inline__table td {
  padding: 8px 12px 8px 0;
  font-size: var(--kf-fs-meta);
  line-height: 1.4;
  color: #1f2d36;
  vertical-align: top;
  border-top: 1px solid #e3ebf3;
  overflow-wrap: anywhere;
}
.kf-inline__table tbody tr:hover > td {
  background: #e9f1f9;
}
.kf-nowrap {
  white-space: nowrap;
}
.kf-strong {
  font-weight: 600;
}
.kf-gap {
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fffaf0;
}
/* In the narrow Why column the labels sit above their values. */
.kf-gap ::v-deep .kf-gap-note__label {
  flex-basis: 100%;
  min-width: 0;
}
.kf-muted {
  color: #54636f;
}
.kf-detail {
  margin-top: 2px;
  font-size: var(--kf-fs-label);
  color: #54636f;
}
.kf-channel {
  display: flex;
  align-items: center;
  gap: 4px;
}
/* Phones: each day becomes a small stacked card, label then value. */
@media (max-width: 599px) {
  .kf-inline {
    padding: 4px 8px 8px 12px;
  }
  .kf-inline__table,
  .kf-inline__table tbody,
  .kf-inline__table tr,
  .kf-inline__table td {
    display: block;
    width: 100%;
  }
  .kf-inline__table colgroup,
  .kf-inline__table thead {
    display: none;
  }
  .kf-inline__table tr {
    padding: 6px 0;
    border-top: 1px solid #e3ebf3;
  }
  .kf-inline__table td {
    border-top: 0;
    padding: 3px 0;
  }
  .kf-inline__table td::before {
    content: attr(data-label);
    display: block;
    font-size: var(--kf-fs-label);
    font-weight: 600;
    color: var(--kf-text-muted);
  }
}
</style>
